/**
 * CustomerSignInModal.jsx
 * 
 * Sign-in modal for marketplace customers.
 * Supports Google SSO and Magic Link (email) authentication.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { toast } from 'sonner';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Google logo SVG
const GoogleLogo = () => (
  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const CustomerSignInModal = ({ open, onOpenChange }) => {
  const { loginWithGoogle } = useCustomerAuth();
  const [view, setView] = useState('main'); // 'main' | 'magic-link' | 'magic-link-sent'
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleSignIn = () => {
    sessionStorage.setItem('customerAuthRedirect', window.location.pathname);
    loginWithGoogle();
  };

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/customer/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email,
          redirect_url: window.location.pathname 
        })
      });

      if (response.ok) {
        setView('magic-link-sent');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send magic link');
      }
    } catch (error) {
      toast.error('Failed to send magic link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetModal = () => {
    setView('main');
    setEmail('');
    setIsLoading(false);
  };

  const handleOpenChange = (newOpen) => {
    if (!newOpen) resetModal();
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">
            {view === 'magic-link-sent' ? 'Check your email' : 'Sign in to your account'}
          </DialogTitle>
        </DialogHeader>

        <div className="py-6">
          {/* Main View - SSO Options */}
          {view === 'main' && (
            <>
              <p className="text-center text-gray-600 mb-6">
                Access your trips, bookings, and reviews
              </p>

              {/* Google SSO - Primary Option */}
              <Button 
                variant="outline" 
                className="w-full h-12 text-base font-medium border-gray-300 hover:bg-gray-50"
                onClick={handleGoogleSignIn}
                data-testid="google-signin-btn"
              >
                <GoogleLogo />
                Continue with Google
              </Button>

              {/* Magic Link Option */}
              <Button 
                variant="outline" 
                className="w-full h-12 text-base font-medium border-gray-300 hover:bg-gray-50 mt-3"
                onClick={() => setView('magic-link')}
                data-testid="magic-link-btn"
              >
                <Mail className="w-5 h-5 mr-3 text-gray-600" />
                Email me a sign-in link
              </Button>

              <p className="mt-6 text-center text-xs text-gray-500">
                By continuing, you agree to our{' '}
                <a href="/terms" className="underline hover:text-gray-700">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" className="underline hover:text-gray-700">Privacy Policy</a>
              </p>

              <div className="relative py-4 mt-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or</span>
                </div>
              </div>

              <p className="text-center text-sm text-gray-600">
                Are you a tour operator?{' '}
                <Link 
                  to="/login" 
                  className="text-blue-600 hover:underline font-medium"
                  onClick={() => handleOpenChange(false)}
                >
                  Sign in here
                </Link>
              </p>
            </>
          )}

          {/* Magic Link Email Form */}
          {view === 'magic-link' && (
            <>
              <button 
                onClick={() => setView('main')}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <p className="text-center text-gray-600 mb-6">
                We&apos;ll email you a secure link to sign in instantly
              </p>

              <form onSubmit={handleMagicLinkRequest} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email address
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12"
                    required
                    data-testid="magic-link-email-input"
                  />
                </div>

                <Button 
                  type="submit"
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700"
                  disabled={isLoading}
                  data-testid="send-magic-link-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send sign-in link
                    </>
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Magic Link Sent Confirmation */}
          {view === 'magic-link-sent' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              
              <p className="text-gray-600 mb-2">
                We sent a sign-in link to
              </p>
              <p className="font-medium text-gray-900 mb-4">
                {email}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Click the link in the email to sign in. The link expires in 15 minutes.
              </p>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setView('magic-link')}
              >
                Didn&apos;t receive it? Try again
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerSignInModal;
